import axios from 'axios'

import useStore from '@/composables/store'
import useAccess from '@/composables/access'

import type { Store } from '@/views/contests/Components/types/global'

export type NotificationConfig = {
  id?: number
  type_slug: 'claim-reward' | 'online' | 'delivery'
  store_ids: number[]
  title?: string
  content?: string
  is_active?: boolean
  message_channel_slug: string
  stores?: Store[]
}

export default function useMerchantNotificationConfigs() {
  const { t } = useI18n()
  const notification = useNotification()
  const { stores } = useLocalStorage()
  const { getAllowedUserStoreIds, getStoresByModule } = useStore()
  const { hasPermissionBySlug } = useAccess()

  const canEditRewards = computed(() => hasPermissionBySlug('reward.update'))

  const merchantNotificationConfigs = ref<NotificationConfig[]>([])

  const merchantClaimRewardNotification = ref<NotificationConfig | undefined>(undefined)

  const longestStoreName = computed(() => {
    return stores.value?.reduce((longest, current) => (current.name.length > longest.name.length ? current : longest), {
      name: ''
    })?.name
  })

  const multiselectNotificationConfigsStoresFormatted = ref([])

  const updateMultiselectStores = async (notificationConfigSlug: string) => {
    multiselectNotificationConfigsStoresFormatted.value = []
    const allowedUserStoreIds = getAllowedUserStoreIds()
    const storesWithLoyalty = await getStoresByModule('loyalty')
    const loyaltyStores = storesWithLoyalty.map((store: any) => ({
      value: store.id,
      label: store.name,
      disabled: !allowedUserStoreIds?.includes(store.id)
    }))
    const notificationConfigStores = await fetchNotificationConfigs()
    const notificationConfigStoresIds = notificationConfigStores?.find(
      (notification) => notification.type_slug === notificationConfigSlug
    )?.store_ids

    multiselectNotificationConfigsStoresFormatted.value = [
      ...loyaltyStores.filter((store) => notificationConfigStoresIds?.includes(store.value)),
      ...loyaltyStores.filter((store) => !notificationConfigStoresIds?.includes(store.value))
    ]
  }

  const fetchNotificationConfigs = async () => {
    try {
      const { data } = await axios.get('/notifications')
      return data.data
    } catch (error) {
      notification.create('error', {
        text: t('pages.loyaltyPage.notifications.fetch.error'),
        timeout: 3000
      })
      throw error
    }
  }

  const defaultClaimRewardNotification = ref<NotificationConfig>({
    type_slug: 'claim-reward',
    store_ids: [],
    title: null,
    content: '',
    is_active: false,
    message_channel_slug: 'sms'
  })

  const initializeMerchantNotificationConfigs = async () => {
    merchantNotificationConfigs.value = await fetchNotificationConfigs()
    merchantClaimRewardNotification.value = merchantNotificationConfigs.value?.find(
      (notification) => notification.type_slug === 'claim-reward'
    )
  }

  const handleRemoveStore = (notificationConfig: NotificationConfig, storeIdToRemove: number) => {
    notificationConfig.store_ids = notificationConfig.store_ids.filter((storeId) => storeId !== storeIdToRemove)
  }

  initializeMerchantNotificationConfigs()

  return {
    canEditRewards,
    defaultClaimRewardNotification,
    fetchNotificationConfigs,
    handleRemoveStore,
    longestStoreName,
    merchantClaimRewardNotification,
    merchantNotificationConfigs,
    multiselectNotificationConfigsStoresFormatted,
    updateMultiselectStores
  }
}
